import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { shows, articles, type Show } from '@/mocks/shows';

function MiniShowCard({ show }: { show: Show }) {
  return (
    <Link
      to={`/article/${show.id}`}
      className="group flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-all cursor-pointer"
    >
      <img
        src={show.coverImage}
        alt={show.title}
        className="w-16 h-12 rounded-md object-cover object-top flex-shrink-0"
      />
      <div className="min-w-0">
        <h4 className="text-sm font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-600 transition-colors">
          {show.title}
        </h4>
        <span className="text-xs text-foreground-400">{show.rating}分</span>
      </div>
    </Link>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);

  const article = useMemo(() => {
    const found = articles.find((a) => a.id === numericId);
    if (found) return found;
    return null;
  }, [numericId]);

  const relatedShow = useMemo(() => {
    return shows.find((s) => s.id === numericId) || null;
  }, [numericId]);

  const relatedShows = useMemo(() => {
    if (!article) return [];
    return shows
      .filter((s) => s.category === article.category && s.id !== numericId)
      .slice(0, 4);
  }, [article, numericId]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading text-foreground-800 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">您要查看的内容不存在或已被移除</p>
            <Link to="/" className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '综艺资讯', href: '/category/hot' },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-400">
                <span>{article.author}</span>
                <span>{article.publishDate}</span>
                <span>{article.views.toLocaleString()} 阅读</span>
              </div>
            </div>

            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full aspect-[16/9] rounded-xl object-cover object-top mb-8"
              title={article.title}
            />

            <div className="prose prose-lg max-w-none">
              {article.content.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>

            {relatedShow && (
              <div className="mt-10 p-5 md:p-6 rounded-xl bg-background-100">
                <h3 className="font-heading text-lg text-foreground-900 mb-3">
                  相关节目
                </h3>
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <img
                    src={relatedShow.coverImage}
                    alt={relatedShow.title}
                    className="w-full sm:w-48 h-32 rounded-lg object-cover object-top"
                  />
                  <div>
                    <h4 className="font-heading text-base text-foreground-800 mb-2">
                      {relatedShow.title}
                    </h4>
                    <p className="text-sm text-foreground-500 mb-2">
                      {relatedShow.host} | {relatedShow.year}年 | {relatedShow.episodes}期 | {relatedShow.rating}分
                    </p>
                    <Link
                      to={`/article/${relatedShow.id}`}
                      className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
                    >
                      查看详情
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-sm"></i>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {relatedShows.length > 0 && (
              <div className="mt-10">
                <h3 className="font-heading text-lg text-foreground-900 mb-4">
                  推荐综艺
                </h3>
                <div className="space-y-2">
                  {relatedShows.map((show) => (
                    <MiniShowCard key={show.id} show={show} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.summary,
            "image": article.coverImage,
            "author": { "@type": "Person", "name": article.author },
            "datePublished": article.publishDate,
            "publisher": {
              "@type": "Organization",
              "name": "星辰影院",
              "url": "https://www.xingchencinema.com"
            }
          })}
        </script>
      </main>
      <Footer />
    </>
  );
}